import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 100px auto 4rem;
  padding: 0 2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};

  h1 {
    font-size: 2.8rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 2rem;
    text-align: center;
    position: relative;
    &::after {
      content: '';
      display: block;
      width: 80px;
      height: 4px;
      background-color: ${props => props.theme.colors.secondary};
      margin: 1rem auto 0;
    }
  }

  h2 {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    margin-top: 3rem;
    margin-bottom: 1rem;
    position: relative;
    padding-bottom: 0.5rem;
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 50px;
      height: 3px;
      background-color: ${props => props.theme.colors.secondary};
    }
  }

  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-top: 2rem;
    margin-bottom: 0.8rem;
  }

  p {
    margin-bottom: 1.5rem;
  }

  ul {
    list-style: disc;
    margin-left: 20px;
    margin-bottom: 1.5rem;
    li {
      margin-bottom: 0.5rem;
    }
  }

  a {
    color: ${props => props.theme.colors.secondary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PrivacyPolicy = () => {
  return (
    <PageContainer
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h1>Privacy Policy</h1>
      <p><strong>Last Updated: December 29, 2025</strong></p>
      <p>We ask that you read this privacy policy carefully as it contains pertinent information on who we are and how we collect, use, store, and share personal information. It outlines your rights and how to contact us and other relevant organizations in the event you have a complaint.</p>

      <h2>1. Who We Are and How to Contact Us</h2>
      <p>L&J Agape Healthcare Ltd ("we," "us," or "our") provides compassionate and professional care services across the West Midlands, United Kingdom. Our mission is to enhance the quality of life through personalized care, including Dementia Care, Personal Care, Mental Health Support, Disability Care, Elderly Care, Live-In Care (24/7), Agency Staffing, and Care Professionals.</p>
      <h3>Our Contact Details:</h3>
      <ul>
        <li><strong>Name:</strong> L&J Agape Healthcare Ltd</li>
        <li><strong>Address:</strong> 1-3 Market Place, Willenhall, WV13 2AA, United Kingdom</li>
        <li><strong>Phone Number:</strong> +44 7874 290538</li>
        <li><strong>Email:</strong> <a href="mailto:enquiries@ljagapehc.co.uk">enquiries@ljagapehc.co.uk</a></li>
      </ul>

      <h2>2. The Type of Personal Information We Collect</h2>
      <p>We currently collect and process the following information:</p>
      <h3>Information provided directly by you:</h3>
      <ul>
        <li>Full Name</li>
        <li>Telephone Number</li>
        <li>Email Address</li>
        <li>Subject of inquiry</li>
        <li>Message content (from contact forms, service inquiries, feedback)</li>
        <li>Information pertinent to your care needs (e.g., health conditions, care preferences, medical history relevant to care provision)</li>
        <li>Information pertinent to your professional qualifications and experience (for Agency Staffing and Care Professionals applications)</li>
        <li>Financial details for payment processing (e.g., bank details for direct debits, billing information)</li>
      </ul>
      <h3>Information we collect indirectly:</h3>
      <ul>
        <li><strong>Website Usage Data:</strong> Information about your visit to our website, including your IP address, browser type, pages visited, time spent on site, and referral sources (collected via Google Analytics and cookies).</li>
        <li><strong>Social Sharing Data:</strong> Information related to when you share pages with social networks (if available on our site).</li>
      </ul>

      <h2>3. How We Get the Personal Information and Why We Have It</h2>
      <p>Most of the personal information we process is provided to us directly by you for one of the following reasons:</p>
      <ul>
        <li><strong>To respond to your inquiries:</strong> When you fill out our general enquiries form or contact us via phone/email.</li>
        <li><strong>To provide and manage our services:</strong> To assess your care needs, develop and implement care plans, arrange care provision, and make reasonable adjustments to ensure personalized service delivery in the West Midlands. This includes both direct care services and the provision/management of Agency Staff and Care Professionals.</li>
        <li><strong>To process payments:</strong> To set up and manage payment arrangements for the care and support services you receive.</li>
        <li><strong>For recruitment and employment purposes:</strong> When you apply for positions as Agency Staff or Care Professionals.</li>
        <li><strong>To improve our website and services:</strong> By tracking pages you visit via Google Analytics and analyzing user behavior.</li>
        <li><strong>To allow you to share pages with social networks:</strong> If you choose to use sharing functions on our website.</li>
        <li><strong>For marketing communications:</strong> To send you information or offers on similar or relevant services, from which you can opt-out at any time.</li>
      </ul>
      <h3>Under the UK General Data Protection Regulation (UK GDPR), the lawful bases we rely on for processing this information are:</h3>
      <ul>
        <li><strong>(a) Your consent:</strong> Where you have given us explicit consent (e.g., for marketing communications or specific social media promotions). You are able to remove your consent at any time by contacting <a href="mailto:enquiries@ljagapehc.co.uk">enquiries@ljagapehc.co.uk</a>.</li>
        <li><strong>(b) We have a contractual obligation:</strong> Where processing is necessary for the performance of a contract for care services or employment with you, or to take steps at your request before entering into such a contract.</li>
        <li><strong>(c) We have a legal obligation:</strong> Where processing is necessary for us to comply with the law (e.g., CQC regulations, financial reporting).</li>
        <li><strong>(d) We have a vital interest:</strong> Where processing is necessary to protect someone's life (e.g., in medical emergencies).</li>
        <li><strong>(e) We have a legitimate interest:</strong> Where processing is necessary for our legitimate business interests, provided these do not override your fundamental rights and freedoms (e.g., website security, improving services, internal record-keeping).</li>
      </ul>

      <h2>4. How We Store Your Personal Information</h2>
      <p>Your information is securely stored. We employ appropriate technical and organizational measures to protect your personal data from unauthorized access, disclosure, alteration, or destruction.</p>
      <h3>Cookies:</h3>
      <p>Cookies are very small text files that are stored on your computer when you visit some websites. We use cookies to help identify your computer so we can tailor your user experience and send you relevant content that we feel you would be interested in. You can disable any cookies already stored on your computer, but this may stop our website from functioning properly.</p>
      <p>Some cookies are deleted when you close your browser. These are known as session cookies. Others remain on your device until they expire or you delete them from your cache. These are known as persistent cookies and enable us to remember things about you as a returning visitor. To find out more about cookies, including how to see what cookies have been set and how to manage and delete them, visit <a href="http://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">www.allaboutcookies.org</a>.</p>
      <h3>Data Retention Periods:</h3>
      <p>We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements.</p>
      <ul>
        <li><strong>Care plans, notes, and records:</strong> 3 years after the termination of the contract or end of care.</li>
        <li><strong>General communications (emails, messages):</strong> 3 years after the termination of the contract or resolution of the inquiry.</li>
        <li><strong>Payment records and direct debit forms:</strong> 6 years from the end of the corresponding financial year (Direct debit forms may be retained indefinitely against indemnity claims).</li>
        <li><strong>Complaints, compliments, or concerns:</strong> 10 years from the closure of the case.</li>
        <li><strong>Records of accidents, incidents, or near misses:</strong>
          <ul>
            <li>Accidents: 3 years</li>
            <li>Minor incidents: 10 years</li>
            <li>Serious incidents: 20 years</li>
          </ul>
        </li>
        <li><strong>Consent for marketing/social media promotion:</strong> 3 years with options to review and extend with further explicit consent.</li>
        <li><strong>Recorded telephone calls (for training/quality assurance/dispute resolution):</strong> 10 months.</li>
        <li><strong>Market research feedback:</strong> 1 year (unless anonymized).</li>
      </ul>
      <p>We will not share any personal information with third-party organizations or individuals unless it is necessary for the provision of our services (e.g., coordinating with healthcare professionals involved in your care, legal/regulatory compliance) or with your explicit consent.</p>

      <h2>5. Your Data Protection Rights</h2>
      <p>Under data protection law, you have specific rights regarding your personal information. These include:</p>
      <ul>
        <li><strong>Your right of access:</strong> You have the right to ask us for copies of your personal information.</li>
        <li><strong>Your right to rectification:</strong> You have the right to ask us to rectify personal information you think is inaccurate. You also have the right to ask us to complete information you think is incomplete.</li>
        <li><strong>Your right to erasure:</strong> You have the right to ask us to erase your personal information in certain circumstances.</li>
        <li><strong>Your right to restriction of processing:</strong> You have the right to ask us to restrict the processing of your personal information in certain circumstances.</li>
        <li><strong>Your right to object to processing:</strong> You have the right to object to the processing of your personal information in certain circumstances.</li>
        <li><strong>Your right to data portability:</strong> You have the right to ask that we transfer the personal information you gave us to another organization, or to you, in certain circumstances.</li>
      </ul>
      <p>You are not required to pay any charge for exercising your rights. If you make a request, we have one month to respond to you.</p>
      <p>Please contact us at <a href="mailto:enquiries@ljagapehc.co.uk">enquiries@ljagapehc.co.uk</a> if you wish to make a request.</p>

      <h2>6. How to Complain</h2>
      <p>If you have any concerns about our use of your personal information, you can make a complaint to us via:</p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:enquiries@ljagapehc.co.uk">enquiries@ljagapehc.co.uk</a></li>
        <li><strong>Post:</strong> L&J Agape Healthcare Ltd, 1-3 Market Place, Willenhall, WV13 2AA, United Kingdom</li>
        <li><strong>Phone:</strong> +44 7874 290538</li>
      </ul>
      <p>You can also complain to the Information Commissioner's Office (ICO) if you are unhappy with how we have used your data.</p>
      <h3>The ICO’s address:</h3>
      <p>Information Commissioner’s Office<br />
      Wycliffe House<br />
      Water Lane<br />
      Wilmslow<br />
      Cheshire<br />
      SK9 5AF</p>
      <p><strong>Helpline number:</strong> 0303 123 1113<br />
      <strong>ICO website:</strong> <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer">https://www.ico.org.uk</a></p>

      <h2>7. Changes to This Privacy Policy</h2>
      <p>This privacy notice was first published on December 29, 2025. We may change this privacy policy and update our website from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy periodically.</p>
    </PageContainer>
  );
};

export default PrivacyPolicy;